import { create } from 'zustand';
import { getTenants } from '../api/generated/tenants/tenants';
import { useToastStore } from './useToastStore';
import { showApiError } from '../errors';
import i18n from '@/i18n/config';
import type {
  FacilityFlowCoreModulesTenantsDtosCreateTenantRequest,
  FacilityFlowCoreModulesTenantsDtosTenantDto,
  FacilityFlowCoreModulesTenantsDtosUpdateTenantRequest,
  FacilityFlowCoreModulesTenantsWorkflowsCreateTenantWorkflowResponse,
} from '../api/generated/models';
import type {
  BusinessPlan,
  TenantStatisticsDto,
} from '../api/types/admin';

type TenantDto = FacilityFlowCoreModulesTenantsDtosTenantDto;
type CreateTenantRequest = FacilityFlowCoreModulesTenantsDtosCreateTenantRequest;
type UpdateTenantRequest = FacilityFlowCoreModulesTenantsDtosUpdateTenantRequest;
export type CreateTenantResponse =
  FacilityFlowCoreModulesTenantsWorkflowsCreateTenantWorkflowResponse;

const {
  getApiV1Tenants,
  postApiV1Tenants,
  putApiV1TenantsId,
  patchApiV1TenantsIdActivate,
  patchApiV1TenantsIdDeactivate,
  patchApiV1TenantsIdPlan,
  getApiV1TenantsId,
  getApiV1TenantsIdStatistics,
} = getTenants();

const t = (key: string) => i18n.t(key);

interface AdminTenantState {
  tenants: TenantDto[];
  statistics: Record<string, TenantStatisticsDto>;
  loading: boolean;
  saving: boolean;
  error: string | null;

  fetchAll: () => Promise<void>;
  getById: (id: string) => Promise<TenantDto | null>;
  fetchStatistics: (id: string) => Promise<void>;
  create: (data: CreateTenantRequest) => Promise<CreateTenantResponse | null>;
  update: (id: string, data: UpdateTenantRequest) => Promise<boolean>;
  activate: (id: string) => Promise<boolean>;
  deactivate: (id: string) => Promise<boolean>;
  changePlan: (
    id: string,
    newPlan: BusinessPlan,
    applyProration: boolean,
  ) => Promise<TenantDto | null>;
}

export const useAdminTenantStore = create<AdminTenantState>((set, get) => ({
  tenants: [],
  statistics: {},
  loading: false,
  saving: false,
  error: null,

  fetchAll: async () => {
    set({ loading: true, error: null });
    try {
      const response = await getApiV1Tenants();
      set({ tenants: response ?? [], loading: false });
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, loading: false });
    }
  },

  getById: async (id) => {
    try {
      const cached = get().tenants.find((tn) => tn.id === id);
      if (cached) return cached;
      const fresh = await getApiV1TenantsId(id);
      if (fresh) {
        set((s) => ({
          tenants: s.tenants.some((tn) => tn.id === fresh.id)
            ? s.tenants.map((tn) => (tn.id === fresh.id ? fresh : tn))
            : [...s.tenants, fresh],
        }));
      }
      return fresh;
    } catch (error) {
      showApiError(error);
      return null;
    }
  },

  fetchStatistics: async (id) => {
    try {
      const stats = await getApiV1TenantsIdStatistics(id);
      set((s) => ({ statistics: { ...s.statistics, [id]: stats } }));
    } catch (error) {
      showApiError(error);
    }
  },

  create: async (data) => {
    set({ saving: true });
    try {
      const created = await postApiV1Tenants(data);
      set({ saving: false });
      await get().fetchAll();
      // Toast moved to the caller — the page shows a follow-up dialog with the
      // generated welcome credentials, so a separate toast would be noise.
      return created ?? null;
    } catch (error) {
      showApiError(error);
      set({ saving: false });
      return null;
    }
  },

  update: async (id, data) => {
    set({ saving: true });
    try {
      const updated = await putApiV1TenantsId(id, data);
      set((s) => ({
        tenants: s.tenants.map((tn) => (tn.id === id ? { ...tn, ...updated } : tn)),
        saving: false,
      }));
      return true;
    } catch (error) {
      showApiError(error);
      set({ saving: false });
      return false;
    }
  },

  activate: async (id) => {
    set({ saving: true });
    try {
      await patchApiV1TenantsIdActivate(id);
      const fresh = await getApiV1TenantsId(id);
      set((s) => ({
        tenants: s.tenants.map((tn) => (tn.id === id ? { ...tn, ...fresh } : tn)),
        saving: false,
      }));
      useToastStore.getState().addToast(t('admin:tenants.messages.activated'), 'success');
      return true;
    } catch (error) {
      showApiError(error);
      set({ saving: false });
      return false;
    }
  },

  deactivate: async (id) => {
    set({ saving: true });
    try {
      await patchApiV1TenantsIdDeactivate(id);
      const fresh = await getApiV1TenantsId(id);
      set((s) => ({
        tenants: s.tenants.map((tn) => (tn.id === id ? { ...tn, ...fresh } : tn)),
        saving: false,
      }));
      useToastStore.getState().addToast(t('admin:tenants.messages.deactivated'), 'success');
      return true;
    } catch (error) {
      showApiError(error);
      set({ saving: false });
      return false;
    }
  },

  changePlan: async (id, newPlan, applyProration) => {
    set({ saving: true });
    try {
      const updated = await patchApiV1TenantsIdPlan(id, {
        newPlan,
        applyProration,
      });
      set((s) => ({
        tenants: s.tenants.map((tn) => (tn.id === id ? { ...tn, ...updated } : tn)),
        saving: false,
      }));
      useToastStore.getState().addToast(t('admin:tenants.changePlan.success'), 'success');
      return updated;
    } catch (error) {
      showApiError(error);
      set({ saving: false });
      return null;
    }
  },
}));
